export class ApiClientError extends Error {
  readonly status?: number;
  readonly code?: string;
  readonly details?: unknown;

  constructor(message: string, options: { status?: number; code?: string; details?: unknown } = {}) {
    super(message);
    this.name = 'ApiClientError';
    this.status = options.status;
    this.code = options.code;
    this.details = options.details;
  }
}

export function parseErrorPayload(text: string, statusText: string): {
  message: string;
  code?: string;
  payload?: unknown;
} {
  let payload: any;
  try {
    payload = text ? JSON.parse(text) : undefined;
  } catch {
    payload = undefined;
  }

  const nestedError = payload?.error;
  const nestedDetail = payload?.detail;
  const code = typeof payload?.code === 'string'
    ? payload.code
    : typeof nestedError?.code === 'string'
      ? nestedError.code
      : typeof nestedDetail?.code === 'string'
        ? nestedDetail.code
        : (nestedError === 'MAINTENANCE_MODE' ? nestedError : undefined);
  const message = typeof nestedDetail === 'string'
    ? nestedDetail
    : typeof nestedDetail?.message === 'string'
      ? nestedDetail.message
      : typeof payload?.message === 'string'
        ? payload.message
        : typeof nestedError?.message === 'string'
          ? nestedError.message
          : typeof nestedError === 'string' && nestedError !== 'MAINTENANCE_MODE'
            ? nestedError
            : text || statusText;

  return { message, code, payload };
}

export async function apiClientErrorFromResponse(response: Response): Promise<ApiClientError> {
  const text = await response.text().catch(() => '');
  const parsed = parseErrorPayload(text, response.statusText);
  return new ApiClientError(parsed.message || response.statusText || `HTTP ${response.status}`, {
    status: response.status,
    code: parsed.code,
    details: parsed.payload,
  });
}

function maintenanceErrorPayload(error: unknown): { message?: string } | null {
  if (error instanceof ApiClientError && error.status === 503 && error.code === 'MAINTENANCE_MODE') {
    return error.details && typeof error.details === 'object' ? error.details as { message?: string } : {};
  }
  const candidate = error as { code?: unknown; details?: unknown } | null;
  const details = candidate?.details;
  if (
    (candidate?.code === 'functions/unavailable' || candidate?.code === 'unavailable')
    && details && typeof details === 'object'
    && (details as { code?: unknown }).code === 'MAINTENANCE_MODE'
  ) {
    return details as { message?: string };
  }
  return null;
}

export function isMaintenanceError(error: unknown): boolean {
  return maintenanceErrorPayload(error) !== null;
}

export function shouldRetryApiError(error: unknown): boolean {
  if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('abort'))) return false;
  if (isMaintenanceError(error)) return false;
  const status = (error as { status?: number })?.status;
  return !(typeof status === 'number' && status >= 400 && status < 500);
}

export function normalizeApiError(error: unknown): {
  message: string;
  isNetwork: boolean;
  isAuth: boolean;
  isMaintenance: boolean;
  status?: number;
  code?: string;
} {
  if (error instanceof Error && (error.name === 'AbortError' || error.message.includes('abort'))) {
    return { message: 'Richiesta annullata.', isNetwork: false, isAuth: false, isMaintenance: false };
  }

  const message = error instanceof Error ? error.message : 'Errore sconosciuto';
  const status = (error as { status?: number })?.status;
  const code = (error as { code?: string })?.code;

  const maintenance = maintenanceErrorPayload(error);
  if (maintenance) {
    return {
      message: maintenance.message || 'Nexus è temporaneamente in manutenzione.',
      isNetwork: false,
      isAuth: false,
      isMaintenance: true,
      status: 503,
      code: 'MAINTENANCE_MODE',
    };
  }
  if (status === 401) {
    return { message: 'Sessione non valida. Accedi di nuovo.', isNetwork: false, isAuth: true, isMaintenance: false, status, code };
  }
  if (status === 403) {
    return { message: 'Non hai i permessi per accedere a questa risorsa.', isNetwork: false, isAuth: false, isMaintenance: false, status, code };
  }
  if (message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('fetch')) {
    return { message: 'Impossibile contattare il servizio. Riprova tra qualche istante.', isNetwork: true, isAuth: false, isMaintenance: false, status, code };
  }
  if (status && status >= 500) {
    return { message: 'Errore del server. Riprova tra qualche istante.', isNetwork: true, isAuth: false, isMaintenance: false, status, code };
  }
  return { message, isNetwork: false, isAuth: false, isMaintenance: false, status, code };
}
